import { html } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/wishlist';
import { selectors as globalSelectors } from '@elab/core/store/global';
import { selectors as configSelectors } from '@elab/core/store/config';
import { getParameterNames, promotionTypes } from '@elab/core/utils/constants';
import { blurActiveElement } from '@elab/core/utils/dom/manipulations';
import helpers from '@elab/core/store/product/helpers';
import { removeExtraLeadingSlashes } from '@elab/core/utils/url';
import { calculateMaxQuantity } from '@elab/core/utils/cart/CartUtils';
import { button } from '@elab/core/utils/htmlTemplates';
import { getStrikeThroughPrice } from '@elab/core/utils/helpers';

import { findInObjectByPath } from '../../../utils/findInObjectByPath';

/**
 * Component is used as a wishlist container
 *
 * @author Zhynzher Oleksandr
 *
 * @attr { String }  [product-label] - Is label for product
 * @attr { String }  [promotion-label] - Is label for promotion
 * @attr { String }  [quantity-label] - Is label for quantity
 * @attr { String }  [price-label] - Is label for price
 * @attr { String }  [variant-label] - Is label for product variant
 * @attr { String }  [add-to-cart-label] - Is label for "add to cart" button
 * @attr { String }  [out-of-stock-label] - Is label for "out of stock" notification
 * @attr { String }  [remove-from-wishlist-label] - Is label for "remove from wishlist" button
 * @attr { String }  [move-product-label] - Is label for "Move to" button
 * @attr { String }  [empty-wishlist-label] - Is label for "empty wishlist" notification
 * @attr { Boolean }  [show-wishlist-name] - define should wishlist name be depicted
 * @attr { Boolean }  [wrap-in-link] - define should product name and image be wrapped in link
 * @attr { Boolean }  [show-description] - define should product description be depicted
 * @attr { Boolean }  [show-added-date] - define should product added date be depicted
 * @attr { Boolean }  [show-variant] - define should product variant be depicted
 * @attr { String }  [added-date-label] -  label before product added date
 * @attr { Boolean }  [show-range-name] - define should product range be depicted
 * @attr { String }  [shared-wishlist-code] - code provided for a public shared wishlist
 * @attr { Boolean }  [show-brand-name] - define should product brand be depicted
 * @attr { String } [add-class] - additional class for styles
 * @attr { Boolean } [wrap-options] - wrapper for the options (variant, quantity, price, add-to-cart) in order to get a big image container
 * @attr { Boolean } [show-notify-me-button] - define should show 'notifyMe' button instead of disabled 'addToCart'
 * @attr { Boolean } [add-to-notify-me-label] - label for 'notifyMe' button
 * @attr { Boolean } [added-to-notify-me-label] - label for 'notifyMe' button in case if already added no notifyMe list
 * @attr { Boolean } [show-add-to-cart-confirmation] - define should be shown confirmation dialog after adding product to cart
 * @attr { String } [remove-product-popup-name] - popup name that is used to confirm product removal
 * @attr { String } [show-more-button-label] - label for 'load more' button
 * @attr { String } [number-text-separator] - label for amount separation
 * @attr { Boolean } [show-more-button] - button for pagination of the products
 * @attr { Boolean } [hide-product-price-label] - define should hide product price labels from 'shoppingList' page
 * @attr { Boolean } [lose-focus-on-load-click] - define should lose focus after click on loadMore button
 * @attr { Boolean} [show-variant-detail] - defines whether variant details (size/color) should be displayed instead of available variants text
 *
 * @attr { String } [currency] - currency
 * */

export class Wishlist extends LightLitElement {
    static get properties() {
        return {
            productLabel: { type: String, attribute: 'product-label' },
            promotionLabel: { type: String, attribute: 'promotion-label' },
            quantityLabel: { type: String, attribute: 'quantity-label' },
            priceLabel: { type: String, attribute: 'price-label' },
            rrpLabel: { type: String, attribute: 'rrp-label' },
            variantLabel: { type: String, attribute: 'variant-label' },
            addToCartLabel: { type: String, attribute: 'add-to-cart-label' },
            outOfStockLabel: { type: String, attribute: 'out-of-stock-label' },
            unavailableCountryLabel: { type: String, attribute: 'unavailable-country-label' },
            removeFromWishlistLabel: {
                type: String,
                attribute: 'remove-from-wishlist-label',
            },
            moveProductLabel: { type: String, attribute: 'move-product-label' },
            emptyWishlistLabel: {
                type: String,
                attribute: 'empty-wishlist-label',
            },
            addedDateLabel: { type: String, attribute: 'added-date-label' },
            showMoreButtonLabel: {
                type: String,
                attribute: 'show-more-button-label',
            },
            numberTextSeparator: {
                type: String,
                attribute: 'number-text-separator',
            },
            showWishlistName: {
                type: Boolean,
                attribute: 'show-wishlist-name',
            },
            showDescription: { type: Boolean, attribute: 'show-description' },
            showVariant: { type: Boolean, attribute: 'show-variant' },
            showAddedDate: { type: Boolean, attribute: 'show-added-date' },
            wrapInLink: { type: Boolean, attribute: 'wrap-in-link' },
            showRangeName: { type: Boolean, attribute: 'show-range-name' },
            showBrandName: { type: Boolean, attribute: 'show-brand-name' },
            showMoreButton: { type: Boolean, attribute: 'show-more-label' },
            hideWishlistHeader: {
                type: Boolean,
                attribute: 'hide-wishlist-header',
            },
            name: { type: String, attribute: false },
            entries: { type: Array, attribute: false },
            privateName: { type: String, attribute: false },
            privateEntries: { type: Array, attribute: false },
            productPage: { type: String, attribute: false },
            sharedName: { type: String, attribute: false },
            sharedOwnerName: { type: String, attribute: false },
            sharedEntries: { type: String, attribute: false },
            wishlistCode: { type: String, attribute: false },
            isErrorState: { type: Boolean, attribute: false },
            currency: { type: String, attribute: 'currency' },
            sharedWishlistCode: {
                type: String,
                attribute: 'shared-wishlist-code',
            },
            addClass: { type: String, attribute: 'add-class' },
            wrapOptions: { type: Boolean, attribute: 'wrap-options' },
            showNotifyMeButton: {
                type: Boolean,
                attribute: 'show-notify-me-button',
            },
            showAddToCartConfirmation: {
                type: Boolean,
                attribute: 'show-add-to-cart-confirmation',
            },
            addToNotifyMeLabel: {
                type: String,
                attribute: 'add-to-notify-me-label',
            },
            addedToNotifyMeLabel: {
                type: String,
                attribute: 'added-to-notify-me-label',
            },
            removeProductPopupName: {
                type: String,
                attribute: 'remove-product-popup-name',
            },
            deliveryDetailsText: {
                type: String,
                attribute: 'delivery-details-text',
            },
            deliveryDetailsTitle: {
                type: String,
                attribute: 'delivery-details-title',
            },
            recipientEmailInfoTitle: {
                type: String,
                attribute: 'recipient-email-title',
            },
            recipientNameTitle: {
                type: String,
                attribute: 'recipient-name-title',
            },
            senderNameTitle: {
                type: String,
                attribute: 'sender-name-title',
            },
            hideProductPriceLabel: {
                type: Boolean,
                attribute: 'hide-product-price-label',
            },
            loseFocusOnLoadClick: {
                type: Boolean,
                attribute: 'lose-focus-on-load-click',
            },
            showVariantDetail: {
                type: Boolean,
                attribute: 'show-variant-detail',
            },
            gtmPageType: {
                type: String,
                attribute: 'gtm-page-type',
            },
            showPromotionLabel: { type: Boolean, attribute: 'show-promotion-label' },
            calculateProductLimit: {
                type: Boolean,
                attribute: 'calculate-product-limit',
            },
            useMemberPrice: {
                type: Boolean,
                attribute: 'use-member-price',
            },
            membershipLabel: {
                type: String,
                attribute: 'membership-label',
            },
            badgeDisabled: { type: Boolean, attribute: 'badge-disabled' },
            omnibusFeatureSwitcher: { type: Boolean, attribute: 'omnibus-feature-switcher' },
            priceStyleClass: { type: String, attribute: 'price-style-color' },
            isGridLayout: { type: Boolean, attribute: 'is-grid-layout' },
            pageSize: { type: Number, attribute: 'page-size' },
            inStockLabel: { type: String, attribute: 'in-stock-label' },
            outOfStockProductLabel: { type: String, attribute: 'out-of-stock-product-label' },
            shareWishlistExpiredTokenMessage: {
                type: String,
                attribute: 'share-wishlist-expired-token-message',
            },
            addToWishlistEnable: { type: Boolean, attribute: 'add-to-wishlist-enable' },
            addToCartEnable: { type: Boolean, attribute: 'add-to-cart-enable' },
            wishlistPageTitle: { type: String, attribute: 'wishlist-page-title' },
            shareWishlistUrlKey: { type: String, attribute: 'share-wishlist-url-key' },
            numberOfResultsLabel: { type: String, attribute: 'number-of-results-label' },
            isLoading: { type: Boolean, attribute: false },
            isThresholdPromotionEnabled: {
                type: Boolean,
                attribute: 'is-threshold-promotion-enabled',
            },
            thresholdPriceLabel: { type: String, attribute: 'threshold-price-label' },
        };
    }

    constructor() {
        super();
        this.entries = [];
        this.privateEntries = [];
        this.sharedEntries = [];
        this.name = '';
        this.privateName = '';
        this.sharedName = '';
        this.sharedOwnerName = '';
        this.showAddedDate = false;
        this.showDescription = false;
        this.showRangeName = false;
        this.showBrandName = false;
        this.showMoreButton = false;
        this.showMoreButtonLabel = '';
        this.numberTextSeparator = '';
        this.priceLabel = '';
        this.rrpLabel = '';
        this.hideWishlistHeader = false;
        this.addedDateLabel = '';
        this.productWantsMaxValue = 15;
        this.sharedWishlistCode = '';
        this.addClass = '';
        this.wrapOptions = false;
        this.keepProductInWishlist = false;
        this.showNotifyMeButton = false;
        this.addToNotifyMeLabel = '';
        this.addedToNotifyMeLabel = '';
        this.removeProductPopupName = '';
        this.productPage = 1;
        this.pageSize = 5;
        this.deliveryDetailsText = '';
        this.deliveryDetailsTitle = '';
        this.recipientEmailInfoTitle = '';
        this.recipientNameTitle = '';
        this.senderNameTitle = '';
        this.currency = '';
        this.hideProductPriceLabel = false;
        this.loseFocusOnLoadClick = false;
        this.showVariantDetail = false;
        this.emptyWishlistLabel = '';
        this.gtmPageType = '';
        this.calculateProductLimit = false;
        this.useMemberPrice = false;
        this.membershipLabel = '';
        this.omnibusFeatureSwitcher = false;
        this.isGridLayout = false;
        this.inStockLabel = '';
        this.outOfStockProductLabel = '';
        this.shareWishlistExpiredTokenMessage = '';
        this.addToCartEnable = false;
        this.addToWishlistEnable = false;
        this.wishlistPageTitle = '';
        this.isErrorState = false;
        this.shareWishlistUrlKey = '';
        this.numberOfResultsLabel = '';
    }

    render() {
        return this.isLoading
            ? html`
                  <e2-when-defined
                      name="wishlist.isLoading"
                      class="wishlist--loading"
                  ></e2-when-defined>
              `
            : html`
                  <div class="wishlist ${this.addClass}">
                      ${this.showWishlistName
                          ? html`
                                <h1
                                    class="${this.sharedOwnerName
                                        ? 'share-wishlist__name'
                                        : 'wishlist__name'}"
                                >
                                    ${this.name}
                                </h1>
                            `
                          : null}
                      ${this.isGridLayout && !this.isErrorState
                          ? html`
                                <p class="share-wishlist__view-amount">
                                    ${this.numberOfResultsLabel.replace(
                                        '{0}',
                                        this.entries.length.toString(),
                                    )}
                                </p>
                            `
                          : null}
                      ${this.entries.length
                          ? html`
                                ${this.hideWishlistHeader ? '' : this.wishlistHeaderTemplate}
                                ${this.wishlistBodyTemplate}
                            `
                          : this.isErrorState
                          ? this.wishlistErrorTemplate
                          : this.wishlistEmptyTemplate}
                      ${this.renderLoadMoreButton()}
                  </div>
              `;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);

        if (
            this.sharedWishlistCode &&
            (_changedProperties.has('sharedName') || _changedProperties.has('sharedEntries'))
        ) {
            this.name = this.sharedName;
            if (this.isGridLayout && this.sharedOwnerName) {
                this.name = `${this.sharedOwnerName.split(' ')[0]}'in ${this.wishlistPageTitle}`;
            }
            this.entries = this.sharedEntries;
            this.productPage = 1;
        } else if (
            !this.sharedWishlistCode &&
            (_changedProperties.has('privateName') || _changedProperties.has('privateEntries'))
        ) {
            this.name = this.privateName;
            this.entries = this.privateEntries;
        }
    }

    firstUpdated() {
        const url = window.location.href;
        if (
            this.shareWishlistUrlKey &&
            url.includes(this.shareWishlistUrlKey) &&
            !this.sharedWishlistCode
        ) {
            const searchUrl = window.location.search;
            const params = new URLSearchParams(searchUrl);
            this.sharedWishlistCode = params.get('code');
        }
        this.dispatchEvent(new CustomEvent('load-wishlist'));
        if (this.sharedWishlistCode) {
            this.dispatchEvent(
                new CustomEvent('load-shared-wishlist', {
                    detail: {
                        sharedCode: this.sharedWishlistCode,
                    },
                }),
            );
        } else {
            this.dispatchEvent(new CustomEvent('load-wishlist'));
        }
        if (this.useMemberPrice) {
            this.dispatchEvent(new CustomEvent('calculate-total-with-member-price'));
        }
    }

    handleLoadMore() {
        this.productPage += 1;

        if (this.loseFocusOnLoadClick) {
            blurActiveElement();
        }
    }

    renderLoadMoreButton() {
        return html`
            <span class="wishlist-product-amount">
                ${this.lastVisibleProduct} ${this.numberTextSeparator} ${this.entries.length}
            </span>
            ${this.showMoreButton && this.lastVisibleProduct < this.entries.length
                ? button(
                      {
                          className: 'wishlist-load-more__button',
                          clickHandler: this.handleLoadMore,
                      },
                      this.showMoreButtonLabel,
                  )
                : null}
        `;
    }

    beforeDisconnect() {
        if (this.sharedWishlistCode) {
            this.dispatchEvent(new CustomEvent('load-wishlist'));
        }
    }

    get wishlistHeaderItems() {
        return [
            {
                label: this.productLabel,
                modificator: 'product',
            },
            {
                label: this.promotionLabel,
                modificator: 'promotion',
            },
            {
                label: this.quantityLabel,
                modificator: 'quantity',
            },
            {
                label: this.priceLabel,
                modificator: 'price',
            },
            {
                label: this.variantLabel,
                modificator: 'variant',
            },
        ];
    }

    getReducedPriceTemplate(oldPrice, newPrice, label, percentDiscount, card) {
        return html`
            ${percentDiscount
                ? html`
                      <div class="wishlist__percent-discount-price">
                          ${this.badgeDisabled || this.omnibusFeatureSwitcher
                              ? null
                              : html`
                                    <div class="wishlist__percent-discount">
                                        ${percentDiscount.reward
                                            ? percentDiscount.reward.formattedRewardValue
                                            : ''}
                                    </div>
                                `}
                          ${this.getRegularPriceTemplate(
                              oldPrice,
                              null,
                              'formatted-price--strikethrough',
                          )}
                      </div>
                  `
                : html`
                      <div class="wishlist__product-price wishlist__product-price--old">
                          ${this.getRegularPriceTemplate(oldPrice)}
                      </div>
                  `}
            ${card && card.membership
                ? html`
                      <span class="wishlist__membership-label">${this.membershipLabel}</span>
                  `
                : ''}
            <e2-price-badge
                class="wishlist__product-price  ${card && card.membership
                    ? 'wishlist__product-price--membership'
                    : 'wishlist__product-price--new'}"
                price="${newPrice}"
                price-label="${label && !this.hideProductPriceLabel ? label : ''}"
                additional-class="formatted-price--in-wishlist"
            >
            </e2-price-badge>
        `;
    }

    getRegularPriceTemplate(price, label, additionalClasses = 'formatted-price--in-wishlist') {
        return html`
            <e2-price-badge
                class="wishlist__product-price"
                price="${price}"
                price-label="${label && !this.hideProductPriceLabel ? label : ''}"
                additional-class="${additionalClasses}"
                price-style-color="${this.priceStyleClass}"
            >
            </e2-price-badge>
        `;
    }

    getPriceWithDiscount(product) {
        const percentDiscount = this.getPercentDiscount(product);
        const memberPrice = product.otherPrices.find(price => price.priceSource === 'MEMBER');
        if (memberPrice) {
            return this.getReducedPriceTemplate(
                product.price.formattedValue,
                memberPrice.formattedValue,
                product.price.priceLabel,
                0,
                { membership: true },
            );
        }

        if (product.price.formattedOldValue || percentDiscount) {
            let strikethroughPrice = product.price.formattedOldValue;

            if (this.omnibusFeatureSwitcher) {
                const priorPrice = product.otherPrices.find(price => price.priceSource === 'PRIOR');
                strikethroughPrice = getStrikeThroughPrice(priorPrice, product.price);
            }

            return this.getReducedPriceTemplate(
                strikethroughPrice,
                product.price.formattedValue,
                product.price.priceLabel,
                percentDiscount,
            );
        }

        return this.getRegularPriceTemplate(product.price.formattedValue, product.price.priceLabel);
    }

    getPercentDiscount(product) {
        if (this.useMarkDownPromotion) {
            const { markdownPromotion = {} } = product;
            if (
                markdownPromotion &&
                markdownPromotion.reward &&
                markdownPromotion.reward.formattedRewardValue
            ) {
                return markdownPromotion;
            }
            return false;
        }
        return (
            product.promotions &&
            product.promotions.find(
                promotion =>
                    promotion.reward &&
                    promotion.reward.rewardType === promotionTypes.percentDiscount,
            )
        );
    }

    promotionTemplate(promotion) {
        const { reward } = promotion;
        return reward && reward.rewardType
            ? html`
                  <div
                      class="wishlist__product-promotion wishlist__product-promotion--${reward.rewardType.toLowerCase()}"
                  ></div>
              `
            : html``;
    }

    get wishlistEmptyTemplate() {
        return html`
            <p
                class="wishlist__message wishlist__message--empty ${this.sharedOwnerName
                    ? 'share-wishlist__empty-message'
                    : ''}"
            >
                ${this.emptyWishlistLabel}
            </p>
        `;
    }

    get wishlistErrorTemplate() {
        return html`
            <div class="share-wishlist__token-error">
                <e2-message bold type="error" css-modifier="shared-wishlist">
                    <p v-slot="message">${this.shareWishlistExpiredTokenMessage}</p>
                </e2-message>
            </div>
        `;
    }

    get wishlistHeaderTemplate() {
        return html`
            <div class="wishlist__header">
                ${this.wishlistHeaderItems.map(item =>
                    item.label
                        ? html`
                              <div
                                  class="wishlist__column wishlist__column--header wishlist__column--${item.modificator}"
                              >
                                  ${item.label}
                              </div>
                          `
                        : null,
                )}
            </div>
        `;
    }

    productImageTemplate(thumbnailImage, productName) {
        return html`
            <e2-product-image
                .images="${thumbnailImage}"
                .productName="${productName}"
                format="list"
                classes="product-image"
            >
            </e2-product-image>
        `;
    }

    configureAddedDate(addDateString) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        const date = new Date(addDateString);
        return `${this.addedDateLabel} ${date.toLocaleDateString('en-US', options)}`;
    }

    getRoundelCategoryStyle(roundelCategoryBadge) {
        return styleMap({
            backgroundColor: roundelCategoryBadge.backgroundColorHexCode,
            color: roundelCategoryBadge.textColorHexCode,
        });
    }

    egiftCardInfoTemplate({
        deliveryDate,
        deliveryTime,
        recipientEmail,
        recipientName,
        senderName,
        message,
    }) {
        const egiftCardInfoItems = [
            {
                desc: this.deliveryDetails(this.deliveryDetailsText, deliveryDate, deliveryTime),
                title: this.deliveryDetailsTitle,
            },
            {
                desc: recipientEmail,
                title: this.recipientEmailInfoTitle,
            },
            {
                desc: senderName,
                title: this.senderNameTitle,
            },
            {
                desc: recipientName,
                title: this.recipientNameTitle,
            },
            {
                desc: message,
            },
        ];
        return html`
            <div v-slot="egiftcard-info">
                ${egiftCardInfoItems.map(item => this.egiftCardInfoItemTemplate(item))}
            </div>
        `;
    }

    egiftCardInfoItemTemplate({ desc, title }) {
        if (!desc) {
            return '';
        }

        return html`
            <e2-wishlist-egiftcard-info
                egift-card-info-desc="${desc}"
                egift-card-info-title="${title || ''}"
            >
            </e2-wishlist-egiftcard-info>
        `;
    }

    deliveryDetails(deliveryDetailsText, date = '', time = '') {
        if (!date && !time) {
            return '';
        }
        return `${deliveryDetailsText} ${date} ${time}`;
    }

    getFullPrice(currency, price) {
        const priceToNumber = +price;
        return `${currency} ${priceToNumber.toFixed(2)}`;
    }

    displayVariantInfo(product) {
        if (this.showVariantDetail) {
            const variantData = product.variantMatrix.find(
                variant => variant.variantOption.code === product.code,
            );

            if (!variantData) return;

            const variantCategoryCode = variantData.parentVariantCategory.code;
            const variantCategoryName = variantData.parentVariantCategory.name;
            const variantValue =
                variantData.variantValueCategory.code || variantData.variantValueCategory.name;

            return html`
                <div class="wishlist__product-variant-detail">
                    <span class="wishlist__product-variant-detail__label">
                        ${variantCategoryName}:
                    </span>
                    ${variantCategoryCode === 'size'
                        ? html`
                              <span class="wishlist__product-variant-detail--size"
                                  >${variantValue}</span
                              >
                          `
                        : html`
                              <span
                                  style="background-color: ${variantValue}"
                                  class="wishlist__product-variant-detail--color"
                              />
                          `}
                </div>
            `;
        }
        return product.variantTypeText;
    }

    get lastVisibleProduct() {
        const visibleRange = this.productPage * this.pageSize;
        return visibleRange > this.entries.length ? this.entries.length : visibleRange;
    }

    isUnavailable = product =>
        product.isOfflineProduct ||
        (Object.prototype.hasOwnProperty.call(product, 'purchasable') && !product.purchasable);

    isForbidden = product => {
        const forbiddenCountries = product.forbiddenCountries;
        if (!forbiddenCountries || !forbiddenCountries.length) {
            return false;
        }
        return forbiddenCountries.findIndex(({ isocode }) => isocode === this.deliveryCountry) > -1;
    };

    renderPromotionBadge(product) {
        if (!(product.roundelCategoryBadges || product.roundelCategoryBadge)) return;
        let badges = [];
        badges =
            product.roundelCategoryBadges && product.roundelCategoryBadges.length
                ? product.roundelCategoryBadges
                : [product.roundelCategoryBadge];
        if (badges.length) {
            badges = [...badges];
            badges.splice(3);
            return html`
                ${badges.map(el => {
                    const localStyle = `max-width: ${el.imageMaxWidth}px; max-height: ${el.imageMaxHeight}px`;
                    return el.image && el.image.url
                        ? html`
                              <img
                                  src="${el.image.url}"
                                  class="wishlist__product-image"
                                  style="${localStyle}"
                              />
                          `
                        : html`
                              <div
                                  style="${this.getRoundelCategoryStyle(el)}"
                                  class="wishlist__product-tag"
                              >
                                  ${el.label}
                              </div>
                          `;
                })}
            `;
        }
    }

    get wishlistBodyTemplate() {
        return html`
            <div class="${this.isGridLayout ? 'wishlist__body-grid' : 'wishlist__body'}">
                ${(this.showMoreButton
                    ? this.entries.slice(0, this.lastVisibleProduct)
                    : this.entries
                ).map(entry => {
                    const { product, egiftCardInfo } = entry;
                    const promotion = product.topPromotion;
                    const promotions = product.promotions;
                    const stockLevel = (product.stock && product.stock.stockLevel) || 0;
                    const brandName = product.masterBrand ? product.masterBrand.name : '';
                    let egiftCardFullPrice = '';
                    if (egiftCardInfo) {
                        egiftCardFullPrice = this.getFullPrice(this.currency, egiftCardInfo.amount);
                    }
                    const redirectUrl = egiftCardInfo
                        ? removeExtraLeadingSlashes(
                              `${product.url}?${getParameterNames.wishlistCodeForAddToCart}=${this.wishlistCode}`,
                          )
                        : '';

                    const maxQuantity = !this.calculateProductLimit
                        ? this.productWantsMaxValue
                        : calculateMaxQuantity(entry, false, false);
                    const isOutOfStock = helpers.isOutOfStock(product);
                    const isProductForbidden = this.isForbidden(product);
                    if (this.isGridLayout) {
                        return html`
                            <e2-wishlist-grid-layout
                                class="share-wishlist__products"
                                .product=${product}
                                ?add-to-wishlist-enable=${this.addToWishlistEnable}
                                ?add-to-cart-enable=${this.addToCartEnable}
                                price-style-color=${this.priceStyleClass}
                                in-stock-label=${this.inStockLabel}
                                out-of-stock-label=${this.outOfStockProductLabel}
                                membership-label=${this.membershipLabel}
                                ?is-threshold-promotion-enabled=${this.isThresholdPromotionEnabled}
                                threshold-price-label=${this.thresholdPriceLabel}
                            >
                            </e2-wishlist-grid-layout>
                        `;
                    }
                    return html`
                        <e2-wishlist-row-layout
                            promotion-label="${this.promotionLabel}"
                            quantity-label="${this.quantityLabel}"
                            price-label="${this.priceLabel}"
                            rrp-label="${this.rrpLabel}"
                            ?wrap-options="${this.wrapOptions}"
                            class="wishlist__body-row"
                        >
                            ${this.wrapInLink
                                ? html`
                                      <e2-link
                                          class="wishlist__product-image-wrapper"
                                          href="${product.url}"
                                          v-slot="product-image"
                                      >
                                          ${this.productImageTemplate(
                                              product.thumbnailImage,
                                              product.name,
                                          )}
                                      </e2-link>
                                  `
                                : html`
                                      <div
                                          class="wishlist__product-image-wrapper"
                                          v-slot="product-image"
                                      >
                                          ${this.productImageTemplate(
                                              product.thumbnailImage,
                                              product.name,
                                          )}
                                      </div>
                                  `}

                            <e2-wishlist-product-info
                                v-slot="product-info"
                                product-name="${product.name || ''}"
                                product-code="${product.code}"
                                product-link="${product.url}"
                                product-range-name="${product.rangeName}"
                                product-brand-name="${brandName}"
                                product-short-description="${product.shortDescription}"
                                product-added-date="${this.configureAddedDate(entry.addedDate)}"
                                .wrapInLink=${this.wrapInLink}
                                .showDescription=${this.showDescription}
                                .showAddedDate=${this.showAddedDate}
                                .showRangeName=${this.showRangeName}
                                .showBrandName=${this.showBrandName}
                                .showVariant=${this.showVariant}
                                .productVariantMatrix="${product.variantMatrix}"
                                ?show-promotion-label="${this.showPromotionLabel}"
                                promotion-label="${(promotion && promotion.title) || ''}"
                            >
                            </e2-wishlist-product-info>
                            ${egiftCardInfo ? this.egiftCardInfoTemplate(egiftCardInfo) : null}
                            <div v-slot="product-quantity">
                                ${product.egiftCard
                                    ? ''
                                    : html`
                                          <e2-wishlist-product-quantity
                                              product-code="${product.code}"
                                              product-wants="${entry.wants}"
                                              product-stock-level="${stockLevel}"
                                              product-min-order-quantity="${product.minOrderQuantity}"
                                              product-max-order-quantity="${this
                                                  .calculateProductLimit
                                                  ? maxQuantity
                                                  : this.productWantsMaxValue}"
                                          >
                                          </e2-wishlist-product-quantity>
                                      `}
                            </div>

                            <e2-wishlist-quantity-counter
                                v-slot="quantity-counter"
                                product-code="${product.code}"
                                product-wants="${entry.wants}"
                                product-wants-max-value="${this.calculateProductLimit
                                    ? maxQuantity
                                    : this.productWantsMaxValue}"
                                ?is-out-of-stock="${isOutOfStock}"
                                ?is-shared-product=${this.sharedWishlistCode}
                            >
                            </e2-wishlist-quantity-counter>
                            <div v-slot="product-promotion">
                                ${promotion && promotion.badge
                                    ? html`
                                          <e2-wishlist-product-promotion
                                              image-url="${promotion.badge.image.url}"
                                              alt-text="${promotion.badge.image.altText}"
                                          >
                                          </e2-wishlist-product-promotion>
                                      `
                                    : null}
                            </div>
                            <div class="wishlist__product-promotions" v-slot="product-promotions">
                                ${promotions && promotions.length
                                    ? html`
                                          ${promotions.map(promotion =>
                                              this.promotionTemplate(promotion),
                                          )}
                                      `
                                    : null}
                            </div>
                            <div v-slot="product-price">
                                ${product.price
                                    ? html`
                                          <e2-price-badge
                                              price="${product.price.formattedValue}"
                                              additional-class="formatted-price--in-wishlist"
                                          >
                                          </e2-price-badge>
                                      `
                                    : null}
                            </div>
                            <div v-slot="product-add-to-cart">
                                ${product.code && !this.isUnavailable(product)
                                    ? html`
                                          <e2-wishlist-add-to-cart
                                              redirect-url="${redirectUrl}"
                                              product-code="${product.code}"
                                              product-wants="${Math.max(
                                                  product.minOrderQuantity,
                                                  entry.wants,
                                              )}"
                                              add-to-cart-label="${this.addToCartLabel}"
                                              out-of-stock-label="${this.outOfStockLabel}"
                                              unavailable-country-label="${this
                                                  .unavailableCountryLabel}"
                                              ?is-forbidden="${isProductForbidden}"
                                              ?is-out-of-stock="${isOutOfStock}"
                                              ?is-shared-product=${this.sharedWishlistCode}
                                              ?show-out-of-stock-button="${!this
                                                  .showNotifyMeButton}"
                                              ?show-confirmation-dialog="${this
                                                  .showAddToCartConfirmation}"
                                              data-label="${this.gtmPageType}"
                                              data-id="${product.code}"
                                              data-base-product="${product.baseProduct}"
                                              data-baseProduct="${product.baseProduct}"
                                              data-brand="${findInObjectByPath(
                                                  product,
                                                  ['masterBrand', 'name'],
                                                  '',
                                              )}"
                                              data-range="${product.rangeName}"
                                              data-price="${findInObjectByPath(
                                                  product,
                                                  ['price', 'value'],
                                                  '',
                                              )}"
                                              data-category="${product.categoryNameHierarchy}"
                                              data-name="${product.name}"
                                          >
                                              <div v-slot="out-of-stock">
                                                  ${this.addToNotifyMeLabel &&
                                                  (isOutOfStock || isProductForbidden)
                                                      ? html`
                                                            <e2-add-to-notify-me
                                                                product-code="${product.code}"
                                                                add-to-notify-me-label="${this
                                                                    .addToNotifyMeLabel}"
                                                                notified-label="${this
                                                                    .addedToNotifyMeLabel}"
                                                                show-popup
                                                            >
                                                            </e2-add-to-notify-me>
                                                        `
                                                      : null}
                                              </div>
                                          </e2-wishlist-add-to-cart>
                                      `
                                    : null}
                            </div>
                            <div v-slot="product-move-item-to">
                                ${product.code
                                    ? html`
                                          <e2-wishlist-move-product
                                              product-code="${product.code}"
                                              move-product-label="${this.moveProductLabel}"
                                          >
                                          </e2-wishlist-move-product>
                                      `
                                    : null}
                            </div>
                            <div v-slot="product-remove">
                                ${product.code && !this.sharedWishlistCode
                                    ? html`
                                          <e2-wishlist-remove-product
                                              product-code="${product.code}"
                                              remove-from-wishlist-label="${this
                                                  .removeFromWishlistLabel
                                                  ? this.removeFromWishlistLabel
                                                  : ''}"
                                              remove-product-popup-name="${this
                                                  .removeProductPopupName
                                                  ? this.removeProductPopupName
                                                  : ''}"
                                          >
                                          </e2-wishlist-remove-product>
                                      `
                                    : null}
                            </div>
                            <div v-slot="product-variant">
                                ${product.variantMatrix && product.variantMatrix.length
                                    ? html`
                                          <e2-wishlist-product-variant
                                              product-code="${product.code}"
                                              .productVariantMatrix=${product.variantMatrix}
                                          >
                                          </e2-wishlist-product-variant>
                                      `
                                    : null}
                            </div>

                            <div
                                class="wishlist__product-price"
                                v-slot="product-price-with-discount"
                            >
                                ${product.price || egiftCardInfo
                                    ? html`
                                          ${egiftCardInfo
                                              ? this.getRegularPriceTemplate(egiftCardFullPrice)
                                              : html`
                                                    ${product.rrpEnabled
                                                        ? html`
                                                              <span
                                                                  class="wishlist__product-price--rrp-label"
                                                                  >${this.rrpLabel}</span
                                                              >
                                                          `
                                                        : ''}${this.getPriceWithDiscount(product)}
                                                `}
                                      `
                                    : html``}
                            </div>
                            <div class="wishlist__product-tag-wrapper" v-slot="product-tag">
                                ${this.renderPromotionBadge(product)}
                            </div>
                            <div
                                class="wishlist__product-variant-type-text"
                                v-slot="product-variant-type-text"
                            >
                                ${product.variantTypeText
                                    ? html`
                                          ${this.displayVariantInfo(product)}
                                      `
                                    : null}
                            </div>
                        </e2-wishlist-row-layout>
                    `;
                })}
            </div>
        `;
    }
}

const mapDispatchToEvents = dispatch => ({
    'load-wishlist': () => dispatch(actions.loadLightWishlist()),
    'load-shared-wishlist': ({ detail }) =>
        dispatch(
            actions.loadSelectedWishlist({
                code: detail.sharedCode,
                sharedWishlist: true,
            }),
        ),
    'calculate-total-with-member-price': () => dispatch(actions.considerMemberPriceForTotal()),
});

const mapStateToProps = state => ({
    privateEntries: selectors.getEntries(state),
    privateName: selectors.getWishlistName(state),
    sharedEntries: selectors.getSharedEntries(state),
    sharedName: selectors.getSharedWishlistName(state),
    sharedOwnerName: selectors.getSharedWishlistOwnerName(state),
    wishlistCode: selectors.getWishlistCode(state),
    deliveryCountry: globalSelectors.getDeliveryCountry(state),
    useMarkDownPromotion: configSelectors.useMarkDownPromotionValue(state),
    isErrorState: selectors.getErrorStateForSharedWishlist(state),
    isLoading: selectors.isLoading(state),
});

export default () =>
    customElements.define('e2-wishlist', connect(mapStateToProps, mapDispatchToEvents)(Wishlist));
